/**
 * Invitee-side hooks for the public invitation flow.
 *
 * - `useInvitationByToken` calls the public `GET /api/invitations/:token`
 *   endpoint and returns the narrow preview shape (family_name,
 *   invited_by_display_name, invite_email, status, expires_at).
 *   No auth cookies required.
 *
 * - `useAcceptInvitation` calls `POST /api/invitations/:token/accept`
 *   which requires authentication. On success, invalidates the
 *   auth cache so the downstream `useAuth` hook immediately sees
 *   the newly-active user + their new family.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, apiFetch } from "@/lib/api";
import { AUTH_QUERY_KEY, type AuthMeResponse } from "@/features/auth/useAuth";
import type { FamilyInvitationStatus } from "@/features/family/useFamilyInvitations";
import { clearPendingInviteToken } from "@/features/invitations/pendingInvite";

/**
 * Public preview shape for `GET /api/invitations/:token`.
 *
 * Mirrors the backend `InspectInvitationResponse` — deliberately
 * narrow, does NOT include the token echo, family_id, inviter's
 * user id, or any other internal identifier.
 */
export interface InvitationPreview {
  family_name: string;
  invited_by_display_name: string;
  invite_email: string;
  status: FamilyInvitationStatus;
  expires_at: string;
}

export interface InvitationLookupResult {
  /** True when the inspect endpoint returned 404. */
  notFound: boolean;
  preview: InvitationPreview | null;
}

export function useInvitationByToken(token: string | undefined) {
  return useQuery<InvitationLookupResult, Error>({
    queryKey: ["invitation", token ?? ""],
    enabled: typeof token === "string" && token.length > 0,
    queryFn: async () => {
      try {
        const preview = await apiFetch<InvitationPreview>(
          `/api/invitations/${encodeURIComponent(token ?? "")}`,
        );
        return { notFound: false, preview };
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) {
          return { notFound: true, preview: null };
        }
        throw error;
      }
    },
    staleTime: 10_000,
  });
}

export function useAcceptInvitation() {
  const queryClient = useQueryClient();
  return useMutation<AuthMeResponse, Error, { token: string }>({
    mutationFn: ({ token }) =>
      apiFetch<AuthMeResponse>(
        `/api/invitations/${encodeURIComponent(token)}/accept`,
        { method: "POST" },
      ),
    onSuccess: (result) => {
      // Seed the auth cache directly with the fresh /me shape the
      // server returned so the next render sees the new family
      // without an extra round-trip.
      queryClient.setQueryData(AUTH_QUERY_KEY, result);
      // Clear any stashed pending token — the flow is complete.
      clearPendingInviteToken();
    },
  });
}
